import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';

import formatters from '../shared/formatters';

const rowLabelSx = {
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  color: '#6B6761',
};

const rowValueSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  textAlign: 'right',
};

const formatTrackerNames = (missingTrackers) => {
  return (missingTrackers || []).map((tracker) => {
    if (tracker === 'CLIENT' || tracker === 'CAREGIVER') {
      return translate(`metrics.ClientMetrics.DataQuality.TrackerNames.${tracker}`);
    }

    return tracker;
  }).join(', ');
};

// Renders only when the snapshot reports at least one data-quality issue; the
// section collapses entirely on clean snapshots.
const DataQualitySection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const dataQuality = currentSnapshot?.dataQuality || null;

  const warningRows = React.useMemo(() => {
    if (!dataQuality) {
      return [];
    }

    const rows = [];

    if (dataQuality.missingClientRateCount > 0) {
      rows.push({
        key: 'MISSING_CLIENT_RATES',
        label: translate('metrics.ClientMetrics.DataQuality.MissingClientRates'),
        value: translate('metrics.ClientMetrics.DataQuality.RateIssueValue', {
          count: formatters.formatCount(dataQuality.missingClientRateCount),
          hours: formatters.formatHours(dataQuality.missingClientRateHours),
        }),
      });
    }

    if (dataQuality.missingCaregiverRateCount > 0) {
      rows.push({
        key: 'MISSING_CAREGIVER_RATES',
        label: translate('metrics.ClientMetrics.DataQuality.MissingCaregiverRates'),
        value: translate('metrics.ClientMetrics.DataQuality.RateIssueValue', {
          count: formatters.formatCount(dataQuality.missingCaregiverRateCount),
          hours: formatters.formatHours(dataQuality.missingCaregiverRateHours),
        }),
      });
    }

    if (dataQuality.unassignedEventCount > 0) {
      rows.push({
        key: 'UNASSIGNED_EVENTS',
        label: translate('metrics.ClientMetrics.DataQuality.UnassignedEvents'),
        value: formatters.formatCount(dataQuality.unassignedEventCount),
      });
    }

    if (dataQuality.skippedEventCount > 0) {
      rows.push({
        key: 'SKIPPED_EVENTS',
        label: translate('metrics.ClientMetrics.DataQuality.SkippedEvents'),
        value: formatters.formatCount(dataQuality.skippedEventCount),
      });
    }

    return rows;
  }, [dataQuality]);

  const skippedEventReasons = React.useMemo(() => {
    if (!dataQuality || !(dataQuality.skippedEventCount > 0)) {
      return [];
    }

    return Object.entries(dataQuality.skippedEventReasons || {}).map(([reason, count]) => ({
      reason,
      count,
    }));
  }, [dataQuality]);

  const missingActualWeeks = dataQuality?.missingActualWeeks || [];

  if (isLoading || !dataQuality || (!warningRows.length && !missingActualWeeks.length)) {
    return null;
  }

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.DataQuality.SectionTitle' />
      <Paper
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
          boxShadow: 'none',
        }}
      >
        <Stack spacing={2}>
          {
            warningRows.length > 0 &&
            <>
              <Alert severity='warning'>
                <Translate expression='metrics.ClientMetrics.DataQuality.WarningAlert' />
              </Alert>
              <Box>
                {
                  warningRows.map((row, index) => (
                    <Stack
                      key={row.key}
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      spacing={2}
                      sx={{
                        py: 1.25,
                        borderBottom: index === warningRows.length - 1 ? 'none' : '1px solid #EAEAEA',
                      }}
                    >
                      <Typography sx={rowLabelSx}>
                        {row.label}
                      </Typography>
                      <Typography sx={rowValueSx}>
                        {row.value}
                      </Typography>
                    </Stack>
                  ))
                }
              </Box>
            </>
          }
          {
            skippedEventReasons.length > 0 &&
            <Box>
              <Typography sx={{...rowLabelSx, mb: 0.75}}>
                <Translate expression='metrics.ClientMetrics.DataQuality.SkippedEventReasons' />
              </Typography>
              <Box component='ul' sx={{m: 0, pl: 2.5}}>
                {
                  skippedEventReasons.map((item) => (
                    <Typography key={item.reason} component='li' sx={{fontSize: '12px', lineHeight: '18px', color: '#1A1916'}}>
                      <Translate
                        expression='metrics.ClientMetrics.DataQuality.SkippedEventReasonItem'
                        substitute={{reason: item.reason, count: formatters.formatCount(item.count)}}
                      />
                    </Typography>
                  ))
                }
              </Box>
            </Box>
          }
          {
            missingActualWeeks.length > 0 &&
            <>
              <Alert severity='info'>
                <Translate expression='metrics.ClientMetrics.DataQuality.InfoAlert' />
              </Alert>
              <Box>
                <Typography sx={{...rowLabelSx, mb: 0.75}}>
                  <Translate expression='metrics.ClientMetrics.DataQuality.MissingActualWeeks' />
                </Typography>
                <Box component='ul' sx={{m: 0, pl: 2.5}}>
                  {
                    missingActualWeeks.map((missingWeek) => (
                      <Typography key={missingWeek.weekOf} component='li' sx={{fontSize: '12px', lineHeight: '18px', color: '#1A1916'}}>
                        <Translate
                          expression='metrics.ClientMetrics.DataQuality.MissingActualWeekItem'
                          substitute={{
                            date: formatters.formatWeekOfDate(missingWeek.weekOf),
                            trackers: formatTrackerNames(missingWeek.missingTrackers),
                          }}
                        />
                      </Typography>
                    ))
                  }
                </Box>
              </Box>
            </>
          }
        </Stack>
      </Paper>
    </Box>
  );
};

DataQualitySection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(DataQualitySection);
